import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { takeUntil, flatMap, tap, map, filter } from 'rxjs/operators';
import { Subject, BehaviorSubject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantService, StreamService } from '../services';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    styles: [`
        .streamsPanel {
            overflow-y: auto;
            height: calc(100vh - 310px);
        }
    `],
    template: `
        <div fxLayout="column">
            <mat-toolbar>
                <h3>Edit Tag</h3>
                <span fxFlex></span>
                <button mat-raised-button color="primary" [disabled]="form.invalid || isUnchanged" (click)="save()">Save</button>
            </mat-toolbar>
            <div style="height: calc(100vh - 132px); overflow-y: auto;">
                <mat-card fxLayoutAlign="space-around start">
                    <mat-card-content [formGroup]="form" fxFlex fxLayout="column" style="padding: 20px;">
                        <div fxFlex fxLayout="column">
                            <mat-form-field>
                                <input matInput placeholder="Label" formControlName="label">
                                <mat-error *ngIf="form.get('label').hasError('required')">Label is required.</mat-error>
                            </mat-form-field>
                        </div>
                        <mat-tab-group>
                            <mat-tab label="Topics and Sources">
                                <div class="streamsPanel"
                                     infiniteScroll 
                                     [infiniteScrollThrottle]="300"
                                     [infiniteScrollDistance]="1"
                                     [scrollWindow]="false"
                                     (scrolled)="nextPage()">
                                    <table mat-table [dataSource]="stream$" style="width: 100%;">
                                        <ng-container matColumnDef="title">
                                            <th mat-header-cell *matHeaderCellDef>Name</th>
                                            <td mat-cell *matCellDef="let row">
                                                <span *ngIf="row.type !== 'COLLECTION'">
                                                    <a [routerLink]="['/settings/shared/sources/', row.id, 'settings']">{{row.title}}</a>
                                                </span>
                                                <span *ngIf="row.type === 'COLLECTION'">
                                                    <a [routerLink]="['/settings/shared/topics/', row.id, 'settings']">{{row.title}}</a>
                                                </span>
                                            </td>
                                        </ng-container>
                                        <ng-container matColumnDef="type">
                                            <th mat-header-cell *matHeaderCellDef>Type</th>
                                            <td mat-cell *matCellDef="let row">
                                                <mat-icon *ngIf="types[row.type]"
                                                          [fontSet]="types[row.type].set" 
                                                          [fontIcon]="types[row.type].icon"
                                                          [matTooltip]="types[row.type].label"></mat-icon>    
                                            </td>
                                        </ng-container>
                                        <ng-container matColumnDef="itemsCount">
                                            <th mat-header-cell *matHeaderCellDef>Items</th>
                                            <td mat-cell *matCellDef="let row">{{row.itemsCount}}</td>
                                        </ng-container>
                                        <ng-container matColumnDef="created">
                                            <th mat-header-cell *matHeaderCellDef>Created</th>
                                            <td mat-cell *matCellDef="let row">{{row.created | date:'shortDate' }}</td>
                                        </ng-container>
                                        <tr mat-header-row *matHeaderRowDef="['title', 'type', 'itemsCount', 'created']"></tr>
                                        <tr mat-row *matRowDef="let row; columns: ['title', 'type', 'itemsCount', 'created'];"></tr>
                                    </table>
                                    <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                                        <spinner></spinner>
                                    </div>
                                    <div *ngIf="stream$ | async as results">
                                        <div *ngIf="!results.length">
                                            <h5 style="text-align: center">
                                                <p>No topics or sources have been tagged with this tag yet.</p> 
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                            </mat-tab>
                        </mat-tab-group>
                    </mat-card-content>
                </mat-card>
            </div>            
        </div>
    `
})
export class SharedTagEditComponent implements OnInit, OnDestroy {
    private cleanupTime = new Subject<void>();
    private readonly attrs = ['label'];
    types = StreamService.types;
    form: FormGroup;
    isUnchanged: boolean;
    tag: any;
    tagId: any;
    noTouchingPaging: boolean;
    param$ = new BehaviorSubject<any>(null);
    stream$ = new BehaviorSubject<any[]>(null);
    lastFetched: any;

    constructor(private fb: FormBuilder, 
                private route: ActivatedRoute,
                private router: Router,
                private snackBar: MatSnackBar,
                private streamService: StreamService,
                private tenantService: TenantService) {}

    ngOnInit(): void {
        this.form = this.fb.group({
            label: ['', Validators.required],
        });

        this.form.valueChanges.pipe(takeUntil(this.cleanupTime))
        .subscribe(() => {
            this.isUnchanged = this.attrs.reduce((accum, key) => 
                accum && ((!this.tag[key] && !this.form.get(key).value) || this.tag[key] === this.form.get(key).value), true);
        });

        this.route.params
        .pipe(
            tap(params => this.tagId = params.tagId),
            flatMap(() => this.tenantService.getCatalogTags()),
            tap(() => this.isUnchanged = true),
            map((tags: any[]) => tags.find(tag => tag.id === this.tagId)),
            tap(tag => this.tag = tag),
            tap(() => this.param$.next({  page: 0,  rows: 40, tagIds: [this.tagId] }) ),
            takeUntil(this.cleanupTime)
        ).subscribe(tag => this.form.patchValue(tag));

        this.param$
        .pipe(
            filter(params => params?.tagIds),
            flatMap(params => this.tenantService.getCatalogTagStreams(this.tagId, params)),
            tap(response => this.lastFetched = response.last),
            tap(() => this.noTouchingPaging = false),
            takeUntil(this.cleanupTime)
        ).subscribe(response => this.stream$.next((this.stream$.value || []).concat(response.content)));
    }

    nextPage() {
        const param$ = this.param$.value;
        param$.page = param$.page + 1;
        this.noTouchingPaging = true;
        this.param$.next(param$);
    }

    save() {
        this.tag.label = this.form.value.label;
        this.tenantService.updateCatalogTag(this.tag).pipe(
            tap(() => this.snackBar.open(`${this.form.value.label} saved.`, 'OK', { duration: 3000}))
        ).subscribe(() => this.router.navigate(['../'], { relativeTo: this.route }));
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}